import React, { ReactElement, Suspense } from "react";
import { LogoLoading } from "../components/atoms/LogoLoading";
import { Navigate, Outlet } from "react-router-dom";
import { ProtectedRoutes, PublicRoutes } from "../types/Routes";
import { AccessToken } from "../enums/access-token";
import SidebarLayout from "../components/layout/SidebarLayout";
import EditCustomer from "@/pages/in-app/EditCustomer";

// pages
const NotFound = React.lazy(() => import("../pages/in-app/NotFound"));
const Dashboard = React.lazy(() => import("../pages/in-app/Dashboard"));
const Customers = React.lazy(() => import("../pages/in-app/Customers"));
const AddCustomer = React.lazy(() => import("../pages/in-app/AddCustomer"));
const PaymentPlans = React.lazy(() => import("../pages/in-app/PaymentPlans"));
const AccountPlans = React.lazy(() => import("../pages/in-app/AccountPlans"));
const PaymentBonuses = React.lazy(
  () => import("../pages/in-app/PaymentBonuses")
);
const AddPaymentPlan = React.lazy(
  () => import("../pages/in-app/AddPaymentPlan")
);
const AddPaymentBonus = React.lazy(
  () => import("../pages/in-app/AddPaymentBonus")
);
const EditPaymentPlan = React.lazy(
  () => import("../pages/in-app/EditPaymentPlan")
);
const Transactions = React.lazy(() => import("../pages/in-app/Transactions"));
const TransactionDetails = React.lazy(
  () => import("../pages/in-app/TransactionDetails")
);
const ActivationDebug = React.lazy(() => import("@/pages/in-app/ActivationDebug"));
const ActivationDebugDetails = React.lazy(
  () => import("@/pages/in-app/ActivationDebugDetails")
);
const Devices = React.lazy(() => import("../pages/in-app/Devices"));
const AddDevice = React.lazy(() => import("../pages/in-app/AddDevice"));
const AddModel = React.lazy(() => import("../pages/in-app/AddModel"));
const AddBrand = React.lazy(() => import("../pages/in-app/AddBrand"));
const EditDevice = React.lazy(() => import("../pages/in-app/EditDevice"));
const EditModel = React.lazy(() => import("../pages/in-app/EditModel"));
const EditBrand = React.lazy(() => import("../pages/in-app/EditBrand"));
const Brands = React.lazy(() => import("../pages/in-app/Brands"));
const Models = React.lazy(() => import("../pages/in-app/Models"));
const Profile = React.lazy(() => import("../pages/in-app/Profile"));
const Kyc = React.lazy(() => import("../pages/in-app/Kyc"));
const RegistrationRequest = React.lazy(
  () => import("../pages/in-app/RegistrationRequest")
);
const DistributorRegisterDetails = React.lazy(
  () => import("../pages/in-app/DistributorRegisterDetails")
);
const DistributorDetails = React.lazy(
  () => import("../pages/in-app/DistributorDetails")
);
const Orders = React.lazy(() => import("../pages/in-app/Orders"));
const OrderDetails = React.lazy(() => import("../pages/in-app/OrderDetails"));
const Products = React.lazy(() => import("../pages/in-app/Products"));
const AllProducts = React.lazy(() => import("../pages/in-app/AllProducts"));
const ProductDetails = React.lazy(
  () => import("../pages/in-app/ProductDetails")
);
const AddProduct = React.lazy(() => import("../pages/in-app/AddProduct"));
const Inventory = React.lazy(() => import("../pages/in-app/Inventory"));
const ManageDistrubutors = React.lazy(
  () => import("../pages/in-app/ManageDistrubutors")
);
const EditPaymentBonusTemplate = React.lazy(
  () => import("../pages/in-app/EditPaymentBonusTemplate")
);
const DonationNetworks = React.lazy(
  () => import("../pages/in-app/DonationNetworks")
);

const InAppRouter = (): ReactElement => {
  const isAuth = localStorage.getItem(AccessToken.KEY);

  return (
    <Suspense fallback={<LogoLoading />}>
      {isAuth ? (
        <div>
          <SidebarLayout>
            <div className="w-full bg-bgLight">
              <Outlet />
            </div>
          </SidebarLayout>
        </div>
      ) : (
        <Navigate to={PublicRoutes.SIGNIN} />
      )}
    </Suspense>
  );
};

const SettingsRouter = (): ReactElement => {
  return <Outlet />;
};

const DistrubutorsRouter = (): ReactElement => {
  return <Outlet />;
};

const DevicesRouter = (): ReactElement => {
  return <Outlet />;
};

const CustomersRouter = (): ReactElement => {
  return <Outlet />;
};

export const protectedRoutes = [
  {
    path: ProtectedRoutes.BASE,
    element: <InAppRouter />,
    children: [
      {
        path: ProtectedRoutes.DASHBOARD,
        name: "Dashboard",
        element: <Dashboard />,
      },
      {
        path: ProtectedRoutes.CUSTOMERS,
        name: "Customers",
        element: <CustomersRouter />,
        children: [
          {
            path: ProtectedRoutes.CUSTOMERS,
            name: "Customers",
            element: <Customers />,
          },
          {
            path: ProtectedRoutes.EDIT_CUSTOMER,
            name: "Edit_Customer",
            element: <EditCustomer />,
          },
          {
            path: ProtectedRoutes.ADD_CUSTOMER,
            name: "Add_Customer",
            element: <AddCustomer />,
          },
        ],
      },
      {
        path: ProtectedRoutes.PAYMENT_PLANS,
        name: "Payment Plans",
        element: <PaymentPlans />,
      },
      {
        path: ProtectedRoutes.PAYMENT_BONUSES,
        name: "Bonus Templates",
        element: <PaymentBonuses />,
      },
      {
        path: ProtectedRoutes.ADD_PAYMENT_PLAN,
        name: "Add_Payment_Plan",
        element: <AddPaymentPlan />,
      },
      {
        path: ProtectedRoutes.ADD_PAYMENT_BONUS,
        name: "Add_Payment_Bonus",
        element: <AddPaymentBonus />,
      },
      {
        path: ProtectedRoutes.EDIT_PAYMENT_PLAN,
        name: "Edit_Payment_Plan",
        element: <EditPaymentPlan />,
      },
      {
        path: ProtectedRoutes.TRANSACTIONS,
        name: "Transactions",
        element: <Transactions />,
      },
      {
        path: ProtectedRoutes.TRANSACTION_DETAILS,
        name: "Transaction Details",
        element: <TransactionDetails />,
      },
      {
        path: ProtectedRoutes.ACTIVATION_DEBUG,
        name: "Activation Debug",
        element: <ActivationDebug />,
      },
      {
        path: ProtectedRoutes.ACTIVATION_DEBUG_DETAILS,
        name: "Activation Debug Details",
        element: <ActivationDebugDetails />,
      },
      {
        path: ProtectedRoutes.ORDERS,
        name: "Orders",
        element: <Orders />,
      },
      {
        path: ProtectedRoutes.ORDER_DETAILS,
        name: "Order Details",
        element: <OrderDetails />,
      },
      {
        path: ProtectedRoutes.PRODUCTS,
        name: "Products",
        element: <Products />,
      },
      {
        path: ProtectedRoutes.ALL_PRODUCTS,
        name: "All Products",
        element: <AllProducts />,
      },
      {
        path: ProtectedRoutes.PRODUCT_DETAILS,
        name: "Product Details",
        element: <ProductDetails />,
      },
      {
        path: ProtectedRoutes.ADD_PRODUCT,
        name: "Add Product",
        element: <AddProduct />,
      },
      {
        path: ProtectedRoutes.INVENTORY,
        name: "Inventory",
        element: <Inventory />,
      },
      {
        path: ProtectedRoutes.DEVICE,
        name: "Device",
        element: <DevicesRouter />,
        children: [
          {
            path: ProtectedRoutes.DEVICES,
            name: "Devices",
            element: <Devices />,
          },
          {
            path: ProtectedRoutes.MODELS,
            name: "Models",
            element: <Models />,
          },
          {
            path: ProtectedRoutes.BRANDS,
            name: "Brands",
            element: <Brands />,
          },
          {
            path: ProtectedRoutes.ADD_DEVICE,
            name: "Add_Device",
            element: <AddDevice />,
          },
          {
            path: ProtectedRoutes.ADD_MODEL,
            name: "Add_Model",
            element: <AddModel />,
          },
          {
            path: ProtectedRoutes.ADD_BRAND,
            name: "Add_Brand",
            element: <AddBrand />,
          },
          {
            path: ProtectedRoutes.EDIT_DEVICE,
            name: "Edit_Device",
            element: <EditDevice />,
          },
          {
            path: ProtectedRoutes.EDIT_MODEL,
            name: "Edit_Model",
            element: <EditModel />,
          },
          {
            path: ProtectedRoutes.EDIT_BRAND,
            name: "Edit_Brand",
            element: <EditBrand />,
          },
        ],
      },
      {
        path: ProtectedRoutes.SETTINGS,
        name: "Settings",
        element: <SettingsRouter />,
        children: [
          {
            path: ProtectedRoutes.PROFILE,
            name: "Profile",
            element: <Profile />,
          },
          {
            path: ProtectedRoutes.KYC,
            name: "Kyc",
            element: <Kyc />,
          },
          {
            path: ProtectedRoutes.ACCOUNT_PLANS,
            name: "Account_Plans",
            element: <AccountPlans />,
          },
          {
            path: ProtectedRoutes.DONATION_NETWORKS,
            name: "Donation_Networks",
            element: <DonationNetworks />,
          },
        ],
      },
      {
        path: ProtectedRoutes.DISTRUBUTORS,
        name: "Distrubutors",
        element: <DistrubutorsRouter />,
        children: [
          {
            path: ProtectedRoutes.MANAGE_DISTRUBUTORS,
            name: "manager-distrubutors",
            element: <ManageDistrubutors />,
          },
          {
            path: ProtectedRoutes.REGISTRATION_REQUEST,
            name: "registration-request",
            element: <RegistrationRequest />,
          },
          {
            path: ProtectedRoutes.DISTRUBUTORS_DETAILS,
            name: "distributor-details",
            element: <DistributorDetails />,
          },
          {
            path: ProtectedRoutes.DISTRUBUTORS_REGISTER_DETAILS,
            name: "distributor-register-details",
            element: <DistributorRegisterDetails />,
          },
        ],
      },
      {
        path: ProtectedRoutes.EDIT_PAYMENT_BONUS_TEMPLATE,
        name: "EditPaymentBonusTemplate",
        element: <EditPaymentBonusTemplate />,
      },
      {
        path: ProtectedRoutes.NOT_FOUND,
        name: "404",
        element: <NotFound />,
      },
    ],
  },
];
